import cn from 'clsx'
import { forwardRef, useEffect } from 'react'
import { useState } from 'react'
import { FieldError } from 'react-hook-form'

interface RenderCustomInputArgs {
  ref: React.Ref<HTMLInputElement>
  className: string
  id?: string
}

export interface TextInputProps extends React.HTMLProps<HTMLInputElement> {
  id?: string
  label: string | undefined
  icon?: React.ReactNode
  error: FieldError | undefined
  value?: string
  renderCustomInput?: (args: RenderCustomInputArgs) => React.ReactNode
  onLabelClick?: () => void
}

export const FormTextInput = forwardRef(
  (
    {
      label,
      id,
      error,
      icon,
      renderCustomInput,
      onLabelClick,
      ...props
    }: TextInputProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
      setIsFocused(Boolean(props.value))
    }, [props.value])

    const htmlForId = id ?? label

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
      <label
        htmlFor={htmlForId}
        className={cn(
          'w-full h-16 border border-deepPurple flex flex-col items-start cursor-pointer',
          {
            'pl-2': !isFocused,
            'justify-start': isFocused,
          }
        )}
        // TODO: handle span positionning with absolute and put the input at
        //       full height in the foreground before it so it can be clicked
        //       directly
        onClick={() => {
          setIsFocused(true)
          onLabelClick !== undefined ? onLabelClick() : null
        }}
      >
        <span
          className={cn(
            'whitespace-nowrap my-5 transition-all flex items-center',
            {
              'my-0 scale-[.75]': isFocused,
              'text-error': error,
            }
          )}
        >
          <div className="mr-2">{icon ?? null}</div>
          {label}
        </span>
        {renderCustomInput ? (
          renderCustomInput({
            ref,
            id: htmlForId,
            className: cn(
              'pl-4 w-full peer outline-none max-h-0 transition-all !bg-transparent !text-inherit',
              {
                'max-h-6': isFocused,
                'text-error': error,
              }
            ),
          })
        ) : (
          <input
            ref={ref}
            id={htmlForId}
            type="text"
            {...props}
            aria-label={label}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(Boolean(props.value))}
            className={cn(
              'pl-4 w-full peer outline-none max-h-0 transition-all !bg-transparent !text-inherit',
              {
                'max-h-6': isFocused,
                'text-error': error,
              }
            )}
          />
        )}
      </label>
    )
  }
)

FormTextInput.displayName = 'FormTextInput'
